/**
 * Slug rewriting for portal and www routes.
 *
 * All route metadata is defined in co-located .meta.ts files next to each route.
 *
 * Used by:
 * - Router rewrites (input: external slug → internal route, output: internal route → locale slug)
 * - Language switcher (translate current slug to target locale)
 * - SEO components (canonical URL and hreflang alternates)
 * - Sitemaps (iterate routes to generate URLs with metadata)
 */
// --- Portal meta files (co-located with each route) -----------------------
import * as portalPrivacyPolicy from '@/routes/$portalId/privacy-policy.meta';
import * as portalLegalDisclaimer from '@/routes/$portalId/legal-disclaimer.meta';

// --- Redesign meta files (co-located with each route) ---------------------
import * as newIndex from '@/routes/new/index.meta';
import * as newSolutions from '@/routes/new/solutions.meta';
import * as newPartnerNgos from '@/routes/new/partner-ngos.meta';
import * as newAboutUs from '@/routes/new/about-us.meta';
import * as newContact from '@/routes/new/contact.meta';

// --- WWW meta files (co-located with each route) --------------------------
import * as wwwIndex from '@/routes/www/index.meta';
import * as aboutUs from '@/routes/www/about-us.meta';
import * as contact from '@/routes/www/contact.meta';
import * as roundUp from '@/routes/www/round-up.meta';
import * as transparency from '@/routes/www/transparency.meta';
import * as liveCounter from '@/routes/www/live-counter.meta';
import * as privacyPolicy from '@/routes/www/privacy-policy.meta';
import * as legalDisclaimer from '@/routes/www/legal-disclaimer.meta';
import * as cookies from '@/routes/www/cookies.meta';
import * as selectionCriteria from '@/routes/www/selection-criteria.meta';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface RouteSlugEntry {
	routeName: string;
	slugs: Record<string, string>;
	/** Sitemap metadata — pages without this field are excluded from the sitemap */
	sitemap?: { changefreq: string; priority: string; lastmod: string };
}

export interface SlugMaps {
	/** External slug (any locale) → internal route segment */
	slugToRoute: Record<string, string>;
	/** Internal route segment → { locale: slug } */
	routeToSlugs: Record<string, Record<string, string>>;
}

// ---------------------------------------------------------------------------
// Map builder
// ---------------------------------------------------------------------------

function buildMaps(routes: RouteSlugEntry[]): SlugMaps {
	const slugToRoute: Record<string, string> = {};
	const routeToSlugs: Record<string, Record<string, string>> = {};

	for (const { routeName, slugs } of routes) {
		routeToSlugs[routeName] = slugs;
		for (const locale in slugs) {
			if (!Object.hasOwn(slugs, locale)) continue;
			slugToRoute[slugs[locale]!] = routeName;
		}
	}

	return { slugToRoute, routeToSlugs };
}

// ---------------------------------------------------------------------------
// Portal routes — add new entries here when creating a portal page with slugs
// ---------------------------------------------------------------------------

export const portalRoutes: RouteSlugEntry[] = [portalPrivacyPolicy, portalLegalDisclaimer];
export const portalSlugs = buildMaps(portalRoutes);

// ---------------------------------------------------------------------------
// WWW routes — add new entries here when creating a www page with slugs
// ---------------------------------------------------------------------------

export const wwwRoutes: RouteSlugEntry[] = [
	wwwIndex,
	aboutUs,
	contact,
	roundUp,
	transparency,
	liveCounter,
	privacyPolicy,
	legalDisclaimer,
	cookies,
	selectionCriteria,
];

export const wwwSlugs = buildMaps(wwwRoutes);

// ---------------------------------------------------------------------------
// Redesign routes (`new.worldcoo.com`) — add new entries here when creating a page with slugs
// ---------------------------------------------------------------------------

export const newRoutes: RouteSlugEntry[] = [newIndex, newSolutions, newPartnerNgos, newAboutUs, newContact];
export const newSlugs = buildMaps(newRoutes);

// ---------------------------------------------------------------------------
// Contexts
// ---------------------------------------------------------------------------

/**
 * Which set of slugs a URL belongs to. It is decided by the HOST — the router's rewrite reads the
 * subdomain and never consults another context's map — so every consumer has to name the same one, or
 * it answers with a slug the host does not serve.
 */
export const SLUG_CONTEXTS = {
	www: wwwSlugs,
	new: newSlugs,
	portal: portalSlugs,
} as const;

export type SlugContext = keyof typeof SLUG_CONTEXTS;

// ---------------------------------------------------------------------------
// Language switcher helper
// ---------------------------------------------------------------------------

/** Reverse map, PER CONTEXT: slug → its locale slugs. */
const slugToSlugsByContext = Object.fromEntries(
	Object.entries(SLUG_CONTEXTS).map(([context, maps]) => {
		const bySlug: Record<string, Record<string, string>> = {};
		for (const route in maps.routeToSlugs) {
			if (!Object.hasOwn(maps.routeToSlugs, route)) continue;
			const slugsByLocale = maps.routeToSlugs[route]!;
			for (const locale in slugsByLocale) {
				if (!Object.hasOwn(slugsByLocale, locale)) continue;
				bySlug[slugsByLocale[locale]!] = slugsByLocale;
			}
		}
		return [context, bySlug];
	}),
) as Record<SlugContext, Record<string, Record<string, string>>>;

/**
 * Translate a page slug to its equivalent in another locale, WITHIN one context.
 *
 * The context argument is not optional politeness — it is the fix for a real bug. This used to read a
 * single map merged across every context, and merging is lossy: `about-us` is both the redesign's own
 * `en` slug and www's `en` slug for `sobre-nosotros`, so switching to Catalan on the redesign's
 * `/es/sobre-nosotros` could answer with a path only the other host serves. Whoever registered last
 * won. Ask for the context you are on and that cannot happen.
 *
 * Returns undefined when the slug is not one this context knows.
 */
export function translateSlug(slug: string, targetLocale: string, context: SlugContext): string | undefined {
	return slugToSlugsByContext[context][slug]?.[targetLocale];
}
